import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { navigate, goBack, showToast, friendlyError } from '../store.js';
import { __ } from '../i18n.js';
import { formatCents } from '../lib/format.js';
import { BILLING_PERIODS, groupSubscriptionsByCategory, subscriptionStatusChip } from '../lib/subscriptions.js';
import Header from '../components/Header.jsx';
import BackButton from '../components/BackButton.jsx';
import Icon from '../components/Icon.jsx';

/**
 * HH-2: Abonnementen list — plan §4.1's UX paragraph: the monthly total as
 * the header number (the "overview of monthly cost" requirement, literally
 * the first thing on screen), a yearly toggle, rows grouped by category,
 * archived rows hidden behind their own filter. Every row's status/
 * switch_from/weeks_until_end and the attention-needed-first sort order
 * are already computed server-side (ETM_Subscriptions_Logic) — this screen
 * only renders what GET /subscriptions returns.
 */
export default function SubscriptionsScreen() {
  const [data, setData] = useState(null);
  const [showYearly, setShowYearly] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  function reload() {
    return api.subscriptions
      .list()
      .then((res) => setData(res))
      .catch((err) => {
        showToast(friendlyError(err), 'error');
        setData({ subscriptions: [], summary: { monthly_total_cents: 0, yearly_total_cents: 0 } });
      });
  }

  useEffect(() => {
    reload();
  }, []);

  if (data === null) {
    return (
      <div>
        <BackButton onClick={goBack} label={__('Meer', 'etm')} />
        <Header title={__('Abonnementen', 'etm')} />
      </div>
    );
  }

  const visible = data.subscriptions.filter((s) => showArchived || !s.archived_at);
  const groups = groupSubscriptionsByCategory(visible);
  const archivedCount = data.subscriptions.filter((s) => s.archived_at).length;
  const total = showYearly ? data.summary.yearly_total_cents : data.summary.monthly_total_cents;

  return (
    <div>
      <BackButton onClick={goBack} label={__('Meer', 'etm')} />
      <Header title={__('Abonnementen', 'etm')} />

      <div class="etm-subscriptions-total">
        <div class="etm-subscriptions-total__amount">{formatCents(total)}</div>
        <div class="etm-subscriptions-total__label">
          {showYearly ? __('per jaar', 'etm') : __('per maand', 'etm')}
        </div>
        <div class="etm-toggle-row etm-subscriptions-total__toggle">
          <button type="button" class={!showYearly ? 'active' : ''} onClick={() => setShowYearly(false)}>
            {BILLING_PERIODS[0].label()}
          </button>
          <button type="button" class={showYearly ? 'active' : ''} onClick={() => setShowYearly(true)}>
            {BILLING_PERIODS[1].label()}
          </button>
        </div>
      </div>

      {archivedCount > 0 ? (
        <div class="etm-form-group etm-checkbox-group">
          <input
            id="etm-sub-show-archived"
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.currentTarget.checked)}
          />
          <label for="etm-sub-show-archived">
            {__('Toon gearchiveerd', 'etm')} ({archivedCount})
          </label>
        </div>
      ) : null}

      {groups.length === 0 ? (
        <div class="etm-empty-state">
          <span class="icon">
            <Icon name="receipt_long" size={44} />
          </span>
          {__('Nog geen abonnementen. Voeg er een toe om je maandlasten in beeld te krijgen.', 'etm')}
        </div>
      ) : (
        groups.map((group) => (
          <div class="etm-subscriptions-group" key={group.category.value}>
            <h3 class="etm-subscriptions-group__title">
              <Icon name={group.category.icon} size={18} /> {group.category.label()}
            </h3>
            <div class="etm-task-list">
              {group.items.map((sub) => {
                const chip = subscriptionStatusChip(sub.status, sub.weeks_until_end);
                const open = () => navigate('subscriptionForm', { subscription: sub });
                return (
                  <div
                    key={sub.id}
                    class={`etm-task-list-item etm-subscription-row${sub.archived_at ? ' etm-subscription-row--archived' : ''}`}
                    role="button"
                    tabIndex={0}
                    aria-label={sub.name}
                    onClick={open}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        open();
                      }
                    }}
                  >
                    <div class="etm-task-list-item__body">
                      <div class="etm-task-list-item__title">
                        {sub.name}
                        {sub.is_shared ? <span class="etm-field-hint"> · {__('gedeeld', 'etm')}</span> : null}
                      </div>
                      <div class="etm-task-list-item__meta">
                        {sub.provider ? `${sub.provider} · ` : ''}
                        {formatCents(sub.cost_cents)}{' '}
                        {sub.billing_period === 'yearly' ? __('/ jaar', 'etm') : __('/ maand', 'etm')}
                      </div>
                    </div>
                    {chip ? (
                      <span class={`etm-chip etm-chip--${chip.tone}`}>{chip.label}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <button
        type="button"
        class="etm-btn etm-btn-primary"
        onClick={() => navigate('subscriptionForm', {})}
      >
        <Icon name="add" size={20} /> {__('Abonnement toevoegen', 'etm')}
      </button>
    </div>
  );
}
